import { Check, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { RichTextInput } from '@avanta/shared/components/chat/RichTextInput';
import { Button } from '@avanta/shared/components/ui/Button';
import { CHAT_BUTTON_CLASSNAME, CHAT_BUTTON_INPUT_CLASSNAME } from '@avanta/shared/constants/chat';
import { useChatTracking } from '@avanta/shared/hooks/chat/useChatTracking';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import { type OptimisticMessage } from '@avanta/shared/types/chat';
import { ChatUpdateType } from '@avanta/shared/types/tracking';
import { getUserId } from '@avanta/shared/utils/storage';
import { stripHtmlTags } from '@avanta/shared/utils/text';

interface EditMessageProps {
  message: OptimisticMessage;
  onClose: () => void;
}

export const EditMessage = ({ message, onClose }: EditMessageProps): React.ReactElement => {
  const [htmlMessage, setHtmlMessage] = useState(message.text ?? '');
  const isEmpty = !stripHtmlTags(htmlMessage);
  const { editMessage } = useMessagesStore();
  const { trackChatMessageUpdated } = useChatTracking();

  const handleSave = useCallback(async (): Promise<void> => {
    if (isEmpty) return;
    await editMessage(message, htmlMessage);
    await trackChatMessageUpdated({
      sessionId: message.channelId,
      senderId: getUserId(),
      updateType: ChatUpdateType.EDITED,
      messageId: message.timetoken,
    });
    onClose();
  }, [editMessage, htmlMessage, isEmpty, message, onClose, trackChatMessageUpdated]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      void handleSave();
    },
    [handleSave]
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col items-end w-full">
        <RichTextInput
          value={htmlMessage}
          isRichTextMode
          initialContent={message.text}
          onHtmlChange={html => setHtmlMessage(html)}
          onSubmit={handleSave}
        />
        <div className="flex items-end">
          <Button
            type="button"
            variant="ghost"
            icon="only"
            onClick={onClose}
            className={CHAT_BUTTON_CLASSNAME}
          >
            <X className="size-4.5" />
          </Button>
          <Button
            variant="ghost"
            type="submit"
            disabled={isEmpty}
            icon="only"
            className={CHAT_BUTTON_INPUT_CLASSNAME}
          >
            <Check className="size-4.5" />
          </Button>
        </div>
      </form>
    </>
  );
};
